import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowUpRight,
  GraduationCap,
  Globe2,
  Scale,
  Lightbulb,
  MapPin,
  Users,
} from "lucide-react";

const maskLine = {
  hidden: { y: "115%" },
  show: (i = 0) => ({
    y: "0%",
    transition: { duration: 1.05, ease: [0.19, 1, 0.22, 1], delay: 0.1 + i * 0.12 },
  }),
};

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease: [0.19, 1, 0.22, 1], delay: i * 0.08 },
  }),
};

/* Real product / team photography */
const HERO_IMG =
  "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=1600&q=80";
const CULTURE_IMG =
  "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1400&q=80";

const PERKS = [
  {
    icon: GraduationCap,
    tag: "01",
    title: "Continuous Learning",
    body: "Sponsored SAP certifications, quarterly craft weeks and a dedicated learning stipend. Grow at the pace of the ecosystem.",
  },
  {
    icon: Globe2,
    tag: "02",
    title: "Global Exposure",
    body: "Deliver alongside partners across India, USA, EU and APAC. Real client rooms, real senior mentorship, no shadow benches.",
  },
  {
    icon: Scale,
    tag: "03",
    title: "Work–Life Balance",
    body: "Hybrid by default, protected weekends, and no rotating on-call unless you opt in. We optimise for stamina, not sprints.",
  },
  {
    icon: Lightbulb,
    tag: "04",
    title: "Innovation Culture",
    body: "Internal R&D pods for BTP, Joule and process mining. Ship your idea into a production accelerator — with your name on it.",
  },
];

const OPEN_ROLES = [
  { role: "SAP S/4HANA Finance Consultant", loc: "Hyderabad · Hybrid", type: "Full-time" },
  { role: "SAP BTP Integration Architect", loc: "Remote (IN / US)", type: "Full-time" },
  { role: "SAP ABAP + Fiori Developer", loc: "Hyderabad · On-site", type: "Full-time" },
  { role: "SAP AMS Lead — Managed Services", loc: "Hyderabad · Hybrid", type: "Full-time" },
];

const MARQUEE_WORDS = [
  "Craft",
  "Curiosity",
  "Ownership",
  "Rigor",
  "Empathy",
  "Excellence",
  "Momentum",
  "Trust",
];

export default function Careers() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const scaleImg = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const yBadge = useTransform(scrollYProgress, [0, 1], [0, -80]);

  return (
    <div data-testid="careers-page" className="bg-[#FAFAFA]">
      {/* ============ HERO ============ */}
      <section
        ref={heroRef}
        className="relative overflow-hidden bg-[#0B1220] text-white min-h-[92vh] flex items-center"
        data-testid="careers-hero"
      >
        {/* Full-bleed background image with parallax */}
        <motion.div
          style={{ y: yImg, scale: scaleImg }}
          className="absolute inset-0 -z-0"
        >
          <img
            src={HERO_IMG}
            alt="Our team at work"
            className="w-full h-full object-cover editorial-img"
            draggable={false}
          />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B1220]/60 via-[#0B1220]/70 to-[#0B1220]" />
        <div className="absolute inset-0 grain" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-32 sm:py-40 w-full">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="flex items-center gap-3 text-[11px] uppercase tracking-[0.32em] text-[#C9A24E]"
          >
            <span className="w-8 h-px bg-[#C9A24E]" />
            Careers — Sunsri Software
          </motion.div>

          <h1 className="font-serif mt-8 text-[54px] sm:text-[92px] lg:text-[124px] leading-[0.92] tracking-[-0.02em] max-w-5xl">
            {["Careers at", "our firm."].map((word, i) => (
              <span key={word} className="block overflow-hidden">
                <motion.span
                  custom={i}
                  variants={maskLine}
                  initial="hidden"
                  animate="show"
                  className="block"
                >
                  {i === 1 ? (
                    <span className="italic text-[#C9A24E]">{word}</span>
                  ) : (
                    word
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <div className="mt-10 max-w-2xl overflow-hidden">
            <motion.p
              variants={maskLine}
              custom={2}
              initial="hidden"
              animate="show"
              className="text-lg sm:text-xl text-white/70 leading-relaxed"
            >
              Join us in shaping the future. From S/4HANA to cloud integrations, your
              next opportunity — and your next great mentor — starts here.
            </motion.p>
          </div>

          {/* stats badge — parallax */}
          <motion.div
            style={{ y: yBadge }}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 1, ease: [0.19, 1, 0.22, 1] }}
            className="mt-16 inline-flex items-stretch border border-white/20 backdrop-blur-md bg-white/[0.04]"
          >
            {[
              { k: "60+", v: "Consultants" },
              { k: "10+", v: "Countries" },
            ].map((s, i) => (
              <div
                key={s.v}
                className={`px-6 sm:px-8 py-5 ${i < 1 ? "border-r border-white/15" : ""}`}
              >
                <div className="font-serif text-3xl sm:text-4xl leading-none">{s.k}</div>
                <div className="mt-2 overline text-white/50">{s.v}</div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* scroll cue */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 0.8 }}
          className="absolute bottom-8 left-6 lg:left-8 flex items-center gap-3 text-[10px] uppercase tracking-[0.32em] text-white/60"
        >
          <motion.span
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
            className="w-px h-10 bg-white/40 block"
          />
          Scroll to explore
        </motion.div>
      </section>

      {/* ============ SLOW EDITORIAL MARQUEE ============ */}
      <section className="relative bg-[#FAFAFA] border-b border-gray-200 overflow-hidden">
        <div className="flex marquee-track whitespace-nowrap py-10">
          {[...MARQUEE_WORDS, ...MARQUEE_WORDS].map((w, i) => (
            <span
              key={i}
              className="font-serif italic text-[64px] sm:text-[92px] leading-none px-10 text-[#0F172A]/10"
            >
              {w}
              <span className="text-[#C9A24E] mx-8">✦</span>
            </span>
          ))}
        </div>
      </section>

      {/* ============ CHAPTER 01 — Join us in shaping the future ============ */}
      <section
        className="relative py-28 sm:py-40 bg-[#FAFAFA]"
        data-testid="careers-chapter-1"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-14 items-center">
            <div className="md:col-span-7">
              <motion.h2
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-80px" }}
                variants={fadeUp}
                custom={1}
                className="font-serif text-4xl sm:text-6xl leading-[1.05] tracking-tight mt-6 text-[#0F172A]"
              >
                Join us in shaping <br />
                <span className="italic text-[#0F172A]/60">the future.</span>
              </motion.h2>

              <motion.p
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-80px" }}
                variants={fadeUp}
                custom={2}
                className="text-lg text-gray-600 mt-8 max-w-xl leading-relaxed"
              >
                We ship high-impact enterprise programs for Fortune 500s and category-defining
                mid-market leaders. If you crave real ownership, senior mentorship, and problems
                that move the needle for millions of users — you&apos;ll feel at home here.
              </motion.p>

              <motion.ul
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-60px" }}
                variants={fadeUp}
                custom={3}
                className="mt-10 space-y-4"
              >
                {[
                  "Direct exposure to senior SAP architects — no shadow benches.",
                  "Client-facing roles from month one; craft grows with responsibility.",
                  "Structured mentorship, quarterly reviews, transparent promotion tracks.",
                ].map((line) => (
                  <li key={line} className="flex items-start gap-3 text-[15px] text-gray-700">
                    <span className="mt-2 w-6 h-px bg-[#C9A24E] shrink-0" />
                    {line}
                  </li>
                ))}
              </motion.ul>

              <motion.div
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-60px" }}
                variants={fadeUp}
                custom={4}
                className="mt-12"
              >
                <Link
                  to="/contact"
                  state={{
                    prefillMessage:
                      "Hi Sunsri team — I'd like to apply for a role at Sunsri Software. Please find my details and preferred role below.",
                    prefillService: "Careers — Application",
                    source: "careers-page",
                  }}
                  data-testid="careers-view-openings-cta"
                  className="btn-gold"
                >
                  View open positions <ArrowUpRight size={16} />
                </Link>
              </motion.div>
            </div>

            {/* Culture image — clipped, spotlit */}
            <div className="md:col-span-5">
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 1.1, ease: [0.19, 1, 0.22, 1] }}
                className="relative"
              >
                <div
                  className="relative h-[540px] overflow-hidden"
                  style={{
                    clipPath: "polygon(0 4%, 100% 0, 100% 96%, 0 100%)",
                  }}
                >
                  <img
                    src={CULTURE_IMG}
                    alt="Sunsri team culture"
                    className="w-full h-full object-cover editorial-img"
                    draggable={false}
                  />
                  {/* spotlight overlay */}
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background:
                        "radial-gradient(60% 60% at 30% 40%, rgba(255,255,255,0) 0%, rgba(11,18,32,0.55) 100%)",
                    }}
                  />
                </div>
                {/* floating annotation */}
                <div className="absolute -bottom-6 -left-6 bg-white border border-gray-200 px-5 py-4 max-w-[260px] shadow-lg">
                  <div className="font-mono text-[10px] tracking-[0.28em] text-[#C9A24E]">
                    NOTE — 001
                  </div>
                  <div className="font-serif text-[15px] leading-snug text-[#0F172A] mt-2">
                    &ldquo;Culture is a compounding asset — we treat it that way.&rdquo;
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ CHAPTER 02 — Why work with us ============ */}
      <section
        className="relative py-28 sm:py-40 bg-[#0B1220] text-white overflow-hidden"
        data-testid="careers-chapter-2"
      >
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 -left-40 w-[600px] h-[600px] rounded-full bg-[#1E3A8A]/25 blur-[140px]" />
          <div className="absolute bottom-20 -right-40 w-[500px] h-[500px] rounded-full bg-[#C9A24E]/10 blur-[140px]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-20">
            <div className="md:col-span-5">
              <motion.h2
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-80px" }}
                variants={fadeUp}
                custom={1}
                className="font-serif text-4xl sm:text-6xl mt-6 leading-[1.05] tracking-tight"
              >
                On your way to success — <br />
                <span className="italic text-[#C9A24E]">why work with us.</span>
              </motion.h2>
            </div>
            <div className="md:col-span-7 md:pt-10">
              <motion.p
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-80px" }}
                variants={fadeUp}
                custom={2}
                className="text-lg text-white/70 leading-relaxed max-w-xl"
              >
                Meaningful projects, senior craft, and a benefits stack designed for long,
                sustainable careers. Four pillars keep us honest.
              </motion.p>
            </div>
          </div>

          {/* 4-card grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0 border-t border-white/15">
            {PERKS.map((p, i) => {
              const Icon = p.icon;
              return (
                <motion.article
                  key={p.title}
                  data-testid={`perk-${p.title.toLowerCase().replace(/\W+/g, "-")}`}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{
                    duration: 0.9,
                    ease: [0.19, 1, 0.22, 1],
                    delay: i * 0.1,
                  }}
                  whileHover={{ y: -6 }}
                  className={`group relative p-8 sm:p-10 border-b border-white/15 ${
                    (i + 1) % 4 !== 0 ? "lg:border-r border-white/15" : ""
                  } ${i % 2 === 0 ? "sm:border-r border-white/15" : ""} transition-colors duration-500 hover:bg-white/[0.03] cursor-default`}
                >
                  <div className="flex items-start justify-between">
                    <span className="font-mono text-xs tracking-[0.28em] text-[#C9A24E]">
                      {p.tag}
                    </span>
                    <Icon
                      size={26}
                      strokeWidth={1.2}
                      className="text-white/60 group-hover:text-[#C9A24E] transition-colors"
                    />
                  </div>
                  <h3 className="font-serif text-2xl sm:text-3xl mt-14 leading-tight">
                    {p.title}
                  </h3>
                  <p className="mt-4 text-[14.5px] leading-relaxed text-white/65">
                    {p.body}
                  </p>
                  <div className="mt-8 w-10 h-px bg-white/30 group-hover:w-16 group-hover:bg-[#C9A24E] transition-all duration-500" />
                </motion.article>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ OPEN ROLES ============ */}
      <section
        className="relative py-28 sm:py-36 bg-[#FAFAFA]"
        data-testid="careers-open-roles"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-14 items-end">
            <div className="md:col-span-8">
              <motion.div
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-80px" }}
                variants={fadeUp}
                className="overline text-[#C9A24E]"
              >
                Current openings
              </motion.div>
              <motion.h3
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-80px" }}
                variants={fadeUp}
                custom={1}
                className="font-serif text-4xl sm:text-5xl mt-4 text-[#0F172A] leading-tight tracking-tight"
              >
                Roles we&apos;re hiring for.
              </motion.h3>
            </div>
            <div className="md:col-span-4 md:text-right">
              <div className="font-mono text-[13px] text-gray-500 tracking-[0.28em]">
                {OPEN_ROLES.length} OPEN · UPDATED WEEKLY
              </div>
            </div>
          </div>

          <div className="border-t border-[#0F172A]/15">
            {OPEN_ROLES.map((r, i) => (
              <motion.div
                key={r.role}
                data-testid={`open-role-${i}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.7, delay: i * 0.06 }}
                className="group relative border-b border-[#0F172A]/15"
              >
                <Link
                  to="/contact"
                  state={{
                    prefillMessage: `Hi Sunsri team — I'd like to apply for the "${r.role}" position (${r.loc}). Please find my details below.`,
                    prefillService: "Careers — Application",
                    source: "careers-open-role",
                  }}
                  className="flex items-center justify-between gap-6 py-6 sm:py-8 transition-all duration-500 group-hover:pl-4"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-4 flex-wrap">
                      <span className="font-mono text-xs tracking-[0.28em] text-[#C9A24E] w-10">
                        0{i + 1}
                      </span>
                      <span className="font-serif text-xl sm:text-3xl text-[#0F172A] leading-tight">
                        {r.role}
                      </span>
                    </div>
                    <div className="mt-3 ml-14 flex items-center gap-5 text-[13px] text-gray-500">
                      <span className="inline-flex items-center gap-1.5">
                        <MapPin size={13} /> {r.loc}
                      </span>
                      <span className="inline-flex items-center gap-1.5">
                        <Users size={13} /> {r.type}
                      </span>
                    </div>
                  </div>
                  <div className="shrink-0 flex items-center gap-3 text-sm font-medium text-[#0F172A]">
                    <span className="hidden sm:inline">Apply</span>
                    <span className="w-10 h-10 rounded-full border border-[#0F172A]/25 flex items-center justify-center group-hover:bg-[#0F172A] group-hover:text-white group-hover:border-[#0F172A] transition-all duration-300">
                      <ArrowUpRight size={16} />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          <div className="mt-14 text-center text-sm text-gray-500 max-w-xl mx-auto">
            Don&apos;t see your role? We&apos;re always meeting exceptional SAP talent —{" "}
            <Link
              to="/contact"
              state={{
                prefillMessage:
                  "Hi Sunsri team — I'm an SAP practitioner interested in future opportunities. Here's a bit about my background:",
                prefillService: "Careers — Application",
                source: "careers-general",
              }}
              className="link-underline text-[#0F172A] font-medium"
              data-testid="careers-open-application"
            >
              send us an open application
            </Link>
            .
          </div>
        </div>
      </section>
    </div>
  );
}
